import React, { useState, useEffect, useMemo } from 'react'
import { Card, Button, Input, Select, Row, Col, Tag, Pagination, Typography, Space, Spin, message, Collapse, Empty } from 'antd'
import { PlusOutlined, SearchOutlined, EditOutlined, EyeOutlined, BookOutlined, FolderOutlined } from '@ant-design/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { listTextbook, type Textbook } from '../../api/manage/textbook'
import { EDUCATION_LEVEL_MAP, CATEGORY_MAP } from '../../utils/textbookParser'
import './TextbookList.css'

const { Option } = Select
const { Title, Paragraph } = Typography
const { Panel } = Collapse

interface TextbookDisplay extends Textbook {
  id: string
  name: string
  tableName?: string
  educationLevel?: string
  category?: string
}

const TextbookList: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterLevel, setFilterLevel] = useState<string>(searchParams.get('level') || 'all')
  const [filterCategory, setFilterCategory] = useState<string>(searchParams.get('category') || 'all')
  const [textbooks, setTextbooks] = useState<TextbookDisplay[]>([])
  const [loading, setLoading] = useState(true)

  // 从API加载教材列表（参考 dkd 页面 getList 方式）
  useEffect(() => {
    getList()
  }, [filterStatus])

  const getList = async () => {
    try {
      setLoading(true)
      const response = await listTextbook({
        pageNum: 1,
        pageSize: 100,
        status: filterStatus !== 'all' ? filterStatus : undefined,
      })

      const textbooksData = response.rows ?? []
      const displayTextbooks: TextbookDisplay[] = textbooksData.map((book) => ({
        ...book,
        id: book.textbookId?.toString() || book.tableName || String(book.id ?? ''),
        name: book.title || book.name || '',
        tableName: book.tableName || `textbook_${book.textbookId || book.id}`,
        category: book.category || book.subject,
        educationLevel: book.educationLevel,
      }))
      setTextbooks(displayTextbooks)
    } catch (error: unknown) {
      console.error('[TextbookList] 加载失败:', error)
      message.warning('加载教材列表失败')
      setTextbooks([])
    } finally {
      setLoading(false)
    }
  }

  /** 搜索（dkd 风格 handleQuery） */
  const handleQuery = () => {
    getList()
  }

  /** 重置筛选条件（dkd 风格 resetQuery） */
  const resetQuery = () => {
    setSearchText('')
    setFilterLevel('all')
    setFilterCategory('all')
    setFilterStatus('all')
    getList()
  }

  // 将subject映射到category
  const mapSubjectToCategory = (subject?: string): string => {
    if (!subject) return 'other'
    const subjectMap: Record<string, string> = {
      '中医基础': 'tcmClinical',
      '中药': 'herbal',
      '中医临床': 'tcmClinical',
      '西医临床': 'westernClinical',
      '哲学': 'philosophy',
      '文化': 'culture',
    }
    return subjectMap[subject] || 'other'
  }

  // 获取分类名称
  const getCategoryName = (category: string): string => {
    const categoryMap: Record<string, string> = {
      'culture': '文化',
      'philosophy': '哲学',
      'tcmClinical': '中医临床',
      'herbal': '中药',
      'westernClinical': '西医临床',
      'intelligent': '智能',
      'equipment': '设备',
      'other': '其他',
    }
    return categoryMap[category] || '其他'
  }

  // 获取教育层次名称
  const getLevelName = (level: string): string => {
    const levelMap: Record<string, string> = {
      'undergraduate': '本科阶段',
      'master': '硕士阶段',
      'doctoral': '博士阶段',
      'continuingEducation': '继续教育',
      'postdoctoral': '博后探索',
      'professionalInsight': '专业感悟',
    }
    return levelMap[level] || '未知'
  }

  // 过滤教材
  const filteredTextbooks = useMemo(() => {
    return textbooks.filter((book) => {
      // 搜索过滤
      if (searchText && !book.name.toLowerCase().includes(searchText.toLowerCase())) {
        return false
      }
      
      // 教育层次过滤
      if (filterLevel !== 'all' && book.educationLevel !== filterLevel) {
        return false
      }
      
      // 分类过滤
      if (filterCategory !== 'all' && book.category !== filterCategory) {
        return false
      }
      
      // 状态过滤
      if (filterStatus !== 'all' && book.status !== filterStatus) {
        return false
      }
      
      return true
    })
  }, [textbooks, searchText, filterLevel, filterCategory, filterStatus])

  // 定义7个分类的固定顺序
  const categoryOrder = ['哲学', '文化', '中医临床', '中药', '西医临床', '智能', '设备']
  
  // 按分类分组，按照固定顺序显示
  const textbooksByCategory = useMemo(() => {
    const grouped: Record<string, TextbookDisplay[]> = {}
    
    filteredTextbooks.forEach((book) => {
      const category = book.category || 'other'
      const categoryName = getCategoryName(category)
      
      if (!grouped[categoryName]) {
        grouped[categoryName] = []
      }
      grouped[categoryName].push(book)
    })
    
    // 按照固定顺序排列分类
    const result: Array<{ category: string; books: TextbookDisplay[] }> = []
    
    // 如果筛选条件是"全部分类"，显示所有7个主要分类（即使没有教材也显示）
    if (filterCategory === 'all') {
      categoryOrder.forEach((categoryName) => {
        result.push({
          category: categoryName,
          books: grouped[categoryName] || []
        })
      })
      
      // 添加其他分类（如果有）
      Object.keys(grouped).forEach((categoryName) => {
        if (!categoryOrder.includes(categoryName)) {
          result.push({
            category: categoryName,
            books: grouped[categoryName]
          })
        }
      })
    } else {
      // 如果筛选了特定分类，只显示该分类
      const selectedCategoryName = getCategoryName(filterCategory)
      if (grouped[selectedCategoryName] && grouped[selectedCategoryName].length > 0) {
        result.push({
          category: selectedCategoryName,
          books: grouped[selectedCategoryName]
        })
      }
    }
    
    return result
  }, [filteredTextbooks, filterCategory])

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      draft: { color: 'default', text: '草稿' },
      reviewing: { color: 'processing', text: '审核中' },
      published: { color: 'success', text: '已发布' },
    }
    const statusInfo = statusMap[status] || { color: 'default', text: status }
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
  }

  if (loading) {
    return (
      <div className="textbook-list-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div className="textbook-list-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-title-wrapper">
            <BookOutlined className="page-title-icon" />
            <Title level={2} className="page-title">智能中医药教材</Title>
          </div>
          <Paragraph className="page-subtitle">
            从数据库加载的教材列表，支持按教育层次和学科分类筛选
          </Paragraph>
        </div>
        <Button 
          type="primary" 
          size="large"
          icon={<PlusOutlined />} 
          onClick={() => navigate('/textbook/new')}
          className="create-btn"
        >
          新建教材
        </Button>
      </div>

      <div className="page-filters">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="搜索教材名称..."
                allowClear
                size="large"
                style={{ width: 'calc(100% - 40px)' }}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value)
                }}
                onPressEnter={() => {}}
              />
              <Button
                size="large"
                icon={<SearchOutlined />}
                type="primary"
                onClick={handleQuery}
              />
            </Space.Compact>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              style={{ width: '100%' }}
              size="large"
              value={filterLevel}
              onChange={(value) => {
                setFilterLevel(value)
              }}
              placeholder="教育层次"
            >
              <Option value="all">全部层次</Option>
              <Option value="undergraduate">本科阶段</Option>
              <Option value="master">硕士阶段</Option>
              <Option value="doctoral">博士阶段</Option>
              <Option value="continuingEducation">继续教育</Option>
              <Option value="postdoctoral">博后探索</Option>
              <Option value="professionalInsight">专业感悟</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              style={{ width: '100%' }}
              size="large"
              value={filterCategory}
              onChange={(value) => {
                setFilterCategory(value)
              }}
              placeholder="学科分类"
            >
              <Option value="all">全部分类</Option>
              <Option value="culture">文化</Option>
              <Option value="philosophy">哲学</Option>
              <Option value="tcmClinical">中医临床</Option>
              <Option value="herbal">中药</Option>
              <Option value="westernClinical">西医临床</Option>
              <Option value="intelligent">智能</Option>
              <Option value="equipment">设备</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              style={{ width: '100%' }}
              size="large"
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value)
              }}
            >
              <Option value="all">全部状态</Option>
              <Option value="draft">草稿</Option>
              <Option value="reviewing">审核中</Option>
              <Option value="published">已发布</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button size="large" onClick={resetQuery}>重置</Button>
          </Col>
        </Row>
      </div>

      <div className="textbook-grid">
        {textbooksByCategory.length > 0 ? (
          textbooksByCategory.map((categoryGroup) => {
            // 如果该分类没有教材且不是7个主要分类之一，则不显示
            if (categoryGroup.books.length === 0 && !categoryOrder.includes(categoryGroup.category)) {
              return null
            }
            
            return (
              <div key={categoryGroup.category} className="category-section">
                <div className="category-header">
                  <FolderOutlined className="category-icon" />
                  <Title level={3} className="category-title">
                    {categoryGroup.category}
                    {categoryGroup.books.length > 0 && (
                      <Tag className="category-count" color="blue">
                        {categoryGroup.books.length} 本
                      </Tag>
                    )}
                  </Title>
                </div>
                {categoryGroup.books.length > 0 ? (
                  <Row gutter={[24, 24]} className="category-books">
                    {categoryGroup.books.map((textbook) => (
                      <Col xs={24} sm={12} lg={8} xl={6} key={textbook.id}>
                        <Card
                          className="textbook-card"
                          cover={
                            <div className="textbook-cover">
                              <div className="cover-placeholder">
                                <BookOutlined style={{ fontSize: 64, color: '#4A90E2' }} />
                              </div>
                            </div>
                          }
                          actions={[
                            <EyeOutlined key="view" onClick={() => navigate(`/textbook/${textbook.tableName}`)} />,
                            <EditOutlined key="edit" onClick={() => navigate(`/textbook/${textbook.tableName}/edit`)} />,
                          ]}
                        >
                          <div className="textbook-info">
                            <h3 className="textbook-title">{textbook.title || textbook.name}</h3>
                            <div className="textbook-meta">
                              {textbook.educationLevel && (
                                <Tag color="green">{getLevelName(textbook.educationLevel)}</Tag>
                              )}
                              {textbook.version && <span className="textbook-version">{textbook.version}</span>}
                              {textbook.subject && <Tag color="blue">{textbook.subject}</Tag>}
                            </div>
                            <div className="textbook-status">
                              {getStatusTag(textbook.status)}
                            </div>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    <Empty description="该分类暂无教材" />
                  </div>
                )}
              </div>
            )
          })
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
            <BookOutlined style={{ fontSize: 64, marginBottom: 16, opacity: 0.3 }} />
            <Paragraph>暂无教材数据</Paragraph>
            {searchText || filterLevel !== 'all' || filterCategory !== 'all' ? (
              <Button onClick={() => {
                setSearchText('')
                setFilterLevel('all')
                setFilterCategory('all')
                setFilterStatus('all')
              }}>
                清除筛选条件
              </Button>
            ) : null}
          </div>
        )}
      </div>

      {filteredTextbooks.length > 0 && (
        <div className="pagination-wrapper">
          <div className="pagination-info">
            <Paragraph style={{ margin: 0, color: '#757575' }}>
              共找到 <strong>{filteredTextbooks.length}</strong> 本教材，分布在 <strong>{textbooksByCategory.length}</strong> 个分类中
            </Paragraph>
          </div>
        </div>
      )}
    </div>
  )
}

export default TextbookList
